import React, { useCallback, useEffect, useRef, useState } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-language_tools";
import {
  FiCheck,
  FiCopy,
  FiLoader,
  FiPlay,
  FiRefreshCw,
  FiTerminal,
  FiTrash2,
} from "react-icons/fi";
import { DS } from "../utils/designSystem";

/* ── Constants ─────────────────────────────────────────────────── */

const STARTER_CODE = `// KodeVedic JavaScript Playground
// Runs directly in your browser — no server needed.

function fibonacci(n) {
  const result = [];
  let [a, b] = [0, 1];
  for (let i = 0; i < n; i++) {
    result.push(a);
    [a, b] = [b, a + b];
  }
  return result;
}

const nums = fibonacci(10);
console.log("Fibonacci:", nums);
console.log("Sum:", nums.reduce((a, b) => a + b, 0));

// Modern JS — destructuring, spread, optional chaining
const user = { name: "KodeVedic", courses: ["Python", "React", "Node.js"] };
const { name, courses: [first, ...rest] } = user;
console.log(\`\\nFirst course: \${first}\`);
console.log("Other courses:", rest);

// Top-level await is supported
const delay = (ms) => new Promise((r) => setTimeout(r, ms));
await delay(50);
console.log("\\nAsync / await works too!");
`;

/* ── Helpers ────────────────────────────────────────────────────── */

/** Serialize a console argument to a readable string */
function fmt(arg) {
  if (arg === null)      return "null";
  if (arg === undefined) return "undefined";
  if (typeof arg === "object") {
    try { return JSON.stringify(arg, null, 2); }
    catch { return String(arg); }
  }
  return String(arg);
}

/** Async-function constructor — supports top-level await */
const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;

/* ── Main Component ─────────────────────────────────────────────── */

function JavaScriptCompiler() {
  const [code, setCode]     = useState(STARTER_CODE);
  const [output, setOutput] = useState([]);   // { text, type: 'log'|'warn'|'error'|'info' }[]
  const [isRunning, setIsRunning] = useState(false);
  const [execMs, setExecMs]       = useState(null);
  const [copied, setCopied]       = useState(false);

  const outputEndRef = useRef(null);

  /* Auto-scroll */
  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [output]);

  const push = useCallback((text, type = "log") => {
    setOutput((prev) => [...prev, { text, type }]);
  }, []);

  /* ── Execute code ── */
  const runCode = async () => {
    if (isRunning) return;
    setIsRunning(true);
    setOutput([]);
    setExecMs(null);

    // Save originals
    const orig = {
      log:   console.log,
      warn:  console.warn,
      error: console.error,
      info:  console.info,
    };

    // Override console methods to capture output
    console.log   = (...a) => push(a.map(fmt).join(" "),  "log");
    console.warn  = (...a) => push(a.map(fmt).join(" "),  "warn");
    console.error = (...a) => push(a.map(fmt).join(" "),  "error");
    console.info  = (...a) => push(a.map(fmt).join(" "),  "info");

    const t0 = performance.now();
    try {
      await new AsyncFunction(code)();
    } catch (e) {
      push(e instanceof Error ? `${e.name}: ${e.message}` : String(e), "error");
    } finally {
      // Restore originals
      console.log   = orig.log;
      console.warn  = orig.warn;
      console.error = orig.error;
      console.info  = orig.info;

      setExecMs((performance.now() - t0).toFixed(1));
      setIsRunning(false);
    }
  };

  /* ── UI actions ── */
  const clearOutput = () => { setOutput([]); setExecMs(null); };
  const resetCode   = () => setCode(STARTER_CODE);
  const copyCode    = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Output line colour by type ── */
  const lineClass = {
    log:   "text-[#A9DC76]",
    info:  "text-blue-300",
    warn:  "text-yellow-50",
    error: "text-red-400",
  };

  /* ── Render ── */
  return (
    <div
      className="flex flex-col bg-richblack-900"
      style={{ height: "calc(100vh - 60px)" }}
    >
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-richblack-700 bg-richblack-800 px-5 py-3">
        {/* Brand */}
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-yellow-50/20 bg-yellow-50/10">
            {/* JS badge */}
            <span className="text-[10px] font-black text-yellow-50 leading-none">JS</span>
          </div>
          <div className="leading-tight">
            <p className="text-sm font-bold text-richblack-5">JavaScript Playground</p>
            <p className="text-[10px] text-richblack-500">Runs in your browser · top-level await supported</p>
          </div>
          <div className="mx-2 h-5 w-px bg-richblack-700" />
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-caribbeangreen-100">
            <FiCheck size={12} />
            Ready
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button onClick={copyCode} className={`${DS.btn.ghost} !py-1.5 !px-3 text-xs`}>
            {copied
              ? <><FiCheck size={12} className="text-caribbeangreen-100" /> Copied</>
              : <><FiCopy size={12} /> Copy</>}
          </button>
          <button onClick={resetCode} className={`${DS.btn.ghost} !py-1.5 !px-3 text-xs`}>
            <FiRefreshCw size={12} /> Reset
          </button>
          <button onClick={clearOutput} className={`${DS.btn.secondary} !py-1.5 !px-3 text-xs`}>
            <FiTrash2 size={12} /> Clear
          </button>
          <button
            onClick={runCode}
            disabled={isRunning}
            className={`${DS.btn.primary} !py-1.5 !px-5 text-sm disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {isRunning
              ? <><FiLoader size={14} className="animate-spin" /> Running…</>
              : <><FiPlay size={14} /> Run</>}
          </button>
        </div>
      </header>

      {/* ── Pane area ───────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden flex-col md:flex-row">

        {/* ── Editor pane ── */}
        <div className="flex flex-1 min-h-0 flex-col border-b border-richblack-700 md:border-b-0 md:border-r md:flex-none md:w-[58%]">
          {/* macOS-style tab bar */}
          <div className="flex flex-shrink-0 items-center gap-2 border-b border-richblack-700 bg-richblack-800/60 px-4 py-1.5">
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F56]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#FFBD2E]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#27C93F]" />
            </div>
            <span className="ml-2 text-[11px] text-richblack-400">main.js</span>
          </div>

          {/* Ace editor */}
          <div className="flex-1 overflow-hidden">
            <AceEditor
              mode="javascript"
              theme="monokai"
              value={code}
              onChange={setCode}
              name="kv-js-editor"
              fontSize={14}
              showPrintMargin={false}
              showGutter={true}
              highlightActiveLine={true}
              width="100%"
              height="100%"
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 2,
                useSoftTabs: true,
                wrap: false,
                fontFamily:
                  "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace",
              }}
            />
          </div>
        </div>

        {/* ── Right pane: output only (no stdin for JS) ── */}
        <div className="flex flex-1 min-h-0 flex-col md:w-[42%]">

          {/* Output header */}
          <div className="flex flex-shrink-0 items-center justify-between border-b border-richblack-700 bg-richblack-800/60 px-4 py-1.5">
            <div className="flex items-center gap-2">
              <FiTerminal size={12} className="text-richblack-400" />
              <span className="text-[11px] text-richblack-400">Console</span>
            </div>
            {execMs !== null && (
              <span className="text-[10px] text-richblack-600">
                Finished in {execMs} ms
              </span>
            )}
          </div>

          {/* Output body */}
          <div className="flex-1 overflow-y-auto bg-[#1e1e1e] p-4 font-mono text-sm leading-relaxed">
            {output.length === 0 && !isRunning && (
              <p className="text-xs text-richblack-600">
                Press{" "}
                <kbd className="rounded bg-richblack-700 px-1 py-0.5 text-richblack-300">
                  Run
                </kbd>{" "}
                to execute your code.
              </p>
            )}
            {output.map((line, i) => (
              <pre
                key={i}
                className={`whitespace-pre-wrap break-words ${lineClass[line.type] ?? lineClass.log}`}
              >
                {line.text}
              </pre>
            ))}
            <div ref={outputEndRef} />
          </div>

          {/* Legend */}
          <div className="flex flex-shrink-0 items-center gap-4 border-t border-richblack-700 bg-richblack-800/40 px-4 py-2">
            <span className="text-[10px] text-richblack-600">console:</span>
            {[
              { label: ".log",   cls: "text-[#A9DC76]" },
              { label: ".info",  cls: "text-blue-300"  },
              { label: ".warn",  cls: "text-yellow-50" },
              { label: ".error", cls: "text-red-400"   },
            ].map(({ label, cls }) => (
              <span key={label} className={`text-[10px] font-mono font-medium ${cls}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default JavaScriptCompiler;
